<template>
  <audit-log
    :is-loading="isLoading"
    :title="`${jackpot.name} Jackpot Audit Log`"
    :entries="auditLog"
    :get-changes="getChanges"
    @close="$emit('close')"
  />
</template>

<script>
import { difference, isEqual } from 'lodash'
import { AuditLog } from '@/components/core/AuditLog'
const NA = 'N/A'
export default {
  name: 'JackpotAuditLogForm',
  components: {
    AuditLog,
  },
  props: {
    isLoading: Boolean,
    auditLog: {
      type: Array,
      required: true,
    },
    jackpot: {
      type: Object,
      required: true,
    },
    currencies: {
      type: Array,
      required: true,
    },
    projectNameById: {
      type: Object,
      required: true,
    },
    gameNameById: {
      type: Object,
      required: true,
    },
    brandNameById: {
      type: Object,
      required: true,
    },
    brands: {
      type: Array,
      required: true,
    },
  },
  emits: ['close'],
  methods: {
    getChanges(logEntry) {
      return this.compareJackpotAuditLogEntry(logEntry)
    },

    compareIncludeExcludeFilters(from, to) {
      from ??= {}
      to ??= {}
      const fromType = !!from.include?.length ? 'include' : 'exclude'
      const toType = !!to.include?.length ? 'include' : 'exclude'

      return {
        hasChanges: fromType !== toType || !isEqual(from[fromType], to[toType]),
        fromType,
        toType,
        from: from[fromType] ?? [],
        to: to[toType] ?? [],
      }
    },
    compareJackpotAuditLogEntry(logEntry) {
      const changes = []
      const pushEntry = (field, from, to) => changes.push(`Changed ${field} from ${from ?? NA} to ${to ?? NA}`)

      if (!Object.keys(logEntry.from).length) {
        changes.push('Created Jackpot')
        return changes
      }

      const { from, to } = logEntry

      if (from.status !== to.status) {
        pushEntry('status', from.status, to.status)
      }

      if (this.isStatusOnly(logEntry)) {
        return changes
      }

      for (const key of ['name', 'start', 'end']) {
        if (from[key] !== to[key]) {
          pushEntry(key, from[key], to[key])
        }
      }

      if (
        from.entities.length !== to.entities.length ||
        !from.entities.every(entity => to.entities.includes(entity))
      ) {
        const addedEntities = difference(to.entities, from.entities)
        const removedEntities = difference(from.entities, to.entities)
        const fmt = entities => entities.map(entityId => this.projectNameById[entityId] || NA).join(', ')

        if (addedEntities.length) {
          changes.push(`Added entities: ${fmt(addedEntities)}`)
        }
        if (removedEntities.length) {
          changes.push(`Removed entities: ${fmt(removedEntities)}`)
        }
      }

      if (from.rules.type !== to.rules.type) {
        pushEntry('rules.type', from.rules.type, to.rules.type)
      }

      for (const bonusField of ['name', 'rollover_type', 'status', 'sticky', 'type', 'wager']) {
        if (from.rules.bonus[bonusField] !== to.rules.bonus[bonusField]) {
          pushEntry(`rules.bonus.${bonusField}`, from.rules.bonus[bonusField], to.rules.bonus[bonusField])
        }
      }

      if (!isEqual(from.rules.bonus.lifetime, to.rules.bonus.lifetime)) {
        // logEntry added for correct variable shadowing
        const from = logEntry.from.rules.bonus.lifetime
        const to = logEntry.to.rules.bonus.lifetime
        const fmt = lifetime => lifetime ? `${lifetime.amount} ${lifetime.period}` : NA

        pushEntry('rules.bonus.lifetime', fmt(from), fmt(to))
      }

      if (!isEqual(from.rules.bonus.max_release, to.rules.bonus.max_release)) {
        const from = logEntry.from.rules.bonus.max_release
        const to = logEntry.to.rules.bonus.max_release
        const fmt = settings => settings ? settings.map(entry => `${entry.amount} ${entry.currency}`).join(', ') : NA

        pushEntry('rules.bonus.max_release', fmt(from), fmt(to))
      }

      const brandChanges = this.compareIncludeExcludeFilters(from.rules.brands, to.rules.brands)
      if (brandChanges.hasChanges) {
        const { from, fromType, to, toType } = brandChanges
        const fmt = brands => brands.map(brandId => this.brandNameById[brandId]).join(', ') || 'none'
        pushEntry('rules.brands', `${fromType} ${fmt(from)}`, `${toType} ${fmt(to)}`)
      }

      // rounds temporarily disabled
      if (['amount', /* 'rounds', */].some(contributionField => from.rules.contribution[contributionField] !== to.rules.contribution[contributionField])) {
        const from = logEntry.from.rules.contribution
        const to = logEntry.to.rules.contribution

        pushEntry('rules.contribution', `amount: ${from.amount}`, `amount: ${to.amount}`)
      }

      if (['amount', 'period'].some(drawField => from.rules.draw[drawField] !== to.rules.draw[drawField])) {
        const from = logEntry.from.rules.draw
        const to = logEntry.to.rules.draw

        pushEntry('rules.draw', `${from.amount} ${from.period}`, `${to.amount} ${to.period}`)
      }

      const gameTypeChanges = this.compareIncludeExcludeFilters(from.rules.game_types, to.rules.game_types)
      if (gameTypeChanges.hasChanges) {
        const { fromType, from, toType, to } = gameTypeChanges
        const fmt = gameTypes => gameTypes.join(', ') || 'none'

        pushEntry('rules.game_types', `${fromType} ${fmt(from)}`, `${toType} ${fmt(to)}`)
      }

      const gameChanges = this.compareIncludeExcludeFilters(from.rules.games, to.rules.games)
      if (gameChanges.hasChanges) {
        const { fromType, from, toType, to } = gameChanges
        const fmt = games => games.map(gameId => this.gameNameById[gameId]).join(', ') || 'none'

        pushEntry('rules.games', `${fromType} ${fmt(from)}`, `${toType} ${fmt(to)}`)
      }

      if (!isEqual(from.rules.prizepool, to.rules.prizepool)) {
        const from = logEntry.from.rules.prizepool
        const to = logEntry.to.rules.prizepool
        const fmt = settings => settings.map(entry => `${entry.amount} ${entry.currency}`).join(', ')

        pushEntry('rules.prizepool', fmt(from), fmt(to))
      }

      if (changes.length === 0) {
        changes.push('No changes')
      }

      return changes
    },

    isStatusOnly(logEntry) {
      return Object.keys(logEntry.from).length === 1 &&
             Object.keys(logEntry.to).length === 1 &&
             'status' in logEntry.from &&
             'status' in logEntry.to
    },
  }
}
</script>
